import { ReactNode } from 'react';
import {
  CalendarDaysIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useSubscriptionContext } from '../../../../../../../providers/SubscriptionProvider/Subscription.context';
import { useUserContext } from '../../../../../../../providers/UserProvider/User.context';
import { formatRetentionOfferAmount } from './RetentionOfferModal/RetentionOfferModal.functions';
import RetentionProgress from './RetentionProgress.component';

export type tRetentionShellProps = {
  isOpen: boolean;
  /**
   * True until the lead count lands. Which opening applies is not knowable before
   * then, so the shell shows its own placeholders rather than letting a caller commit
   * to a heading it may have to take back a moment later.
   */
  isLoading?: boolean;
  /** Eyebrow + heading + supporting line for the left column. Omitted while loading. */
  heading?: string;
  intro?: string;
  /** The rows under the intro: figures for an active workspace, next steps for an empty one. */
  children?: ReactNode;
  /** Shown under the rows when there is something to say about the team. */
  footnote?: ReactNode;
  onKeep: () => void;
  onContinue: () => void;
  onClose: () => void;
  isBusy?: boolean;
};

/**
 * The shared chrome of the cancellation flow's opening step: activity on the left,
 * the plan about to be lost on the right.
 *
 * Both openings render through this on purpose. They used to be two modals of
 * different widths (880px and 631px), and because the picker only learns whether the
 * workspace is empty AFTER the leads load, the customer saw one box swap for a
 * differently sized one mid-render — which read as the modal overlapping itself.
 * Sharing the shell means the variants cannot drift apart again.
 */
export default function RetentionShell(props: tRetentionShellProps) {
  const {
    isOpen,
    isLoading = false,
    heading,
    intro,
    children,
    footnote,
    onKeep,
    onContinue,
    onClose,
    isBusy = false,
  } = props;
  const { t, i18n } = useTranslation('settings/SubscriptionManager');
  const { currentUser } = useUserContext();
  const { subscription } = useSubscriptionContext();

  const planName = t(`page.Plans.${subscription.planCode}`);
  const amount = formatRetentionOfferAmount({
    amount: subscription.totalAmount,
    currency: subscription.currency,
    locale: i18n.language,
  });
  const accessUntil = subscription.endDate
    ? new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(subscription.endDate))
    : null;
  const firstName = currentUser?.name?.split(' ')[0] ?? '';

  return (
    <Modal open={isOpen} className="max-w-[880px] rounded-lg p-0">
      <div className="flex flex-col md:flex-row">
        <section className="flex flex-1 flex-col gap-5 p-8">
          <RetentionProgress current={1} />

          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              {t('cancelSubscription.retention.eyebrow')}
            </p>
            {isLoading ? (
              <>
                <div
                  className="h-9 w-3/4 animate-pulse rounded bg-neutral-300"
                  data-testid="retention-heading-skeleton"
                />
                <div className="h-5 w-full animate-pulse rounded bg-neutral-200" />
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold leading-tight text-neutral-800">
                  {heading}
                </h2>
                <p className="text-base text-neutral-600">{intro}</p>
              </>
            )}
          </div>

          <div className="flex flex-col overflow-hidden rounded-lg border border-neutral-300">
            {isLoading
              ? [0, 1, 2].map((row) => (
                  <div
                    key={row}
                    className={`flex items-center gap-3 px-4 py-3 ${
                      row % 2 === 0 ? 'bg-base-200' : 'bg-base-100'
                    }`}
                    data-testid="retention-row-skeleton"
                  >
                    <span className="h-8 w-8 flex-shrink-0 animate-pulse rounded-lg bg-neutral-300" />
                    <span className="h-5 flex-1 animate-pulse rounded bg-neutral-200" />
                  </div>
                ))
              : children}
          </div>

          {footnote ? (
            <p className="text-sm text-neutral-600">{footnote}</p>
          ) : null}
        </section>

        <aside className="relative flex flex-col justify-center gap-4 bg-base-200 p-6 pt-14 md:w-[320px]">
          <Button
            color="ghost"
            size="sm"
            shape="circle"
            className="absolute right-3 top-3"
            onClick={onClose}
            disabled={isBusy}
            aria-label={t('cancelSubscription.retention.close')}
            data-testid="retention-modal-close-button"
          >
            <XMarkIcon aria-hidden="true" className="h-5 w-5" />
          </Button>

          <p className="text-sm text-neutral-600">
            {t('cancelSubscription.retention.currentPlan')}
          </p>

          <div className="flex flex-col gap-3 rounded-lg bg-base-100 p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-base font-semibold text-primary-content">
                {firstName.charAt(0).toUpperCase()}
              </span>
              <div className="flex flex-col">
                <span className="text-base font-semibold text-neutral-800">
                  {firstName}
                </span>
                <span className="text-sm text-neutral-600">{planName}</span>
              </div>
            </div>

            {amount ? (
              <p className="text-neutral-800">
                <span className="text-2xl font-bold">{amount}</span>
                <span className="text-sm text-neutral-600">
                  {' '}
                  {t('cancelSubscription.retention.perMonth')}
                </span>
              </p>
            ) : null}

            <p className="flex items-center gap-2 text-sm text-neutral-600">
              <UsersIcon aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
              {t('cancelSubscription.retention.seatsIncluded', {
                count: subscription.includedSeats,
              })}
            </p>
          </div>

          {accessUntil ? (
            <p className="flex items-start gap-2 text-sm text-neutral-600">
              <CalendarDaysIcon
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 flex-shrink-0"
              />
              {t('cancelSubscription.retention.accessUntil', {
                date: accessUntil,
              })}
            </p>
          ) : null}
        </aside>
      </div>

      <Modal.Actions className="flex flex-row items-center justify-end gap-4 bg-base-100 px-8 py-5">
        <Button
          color="ghost"
          size="lg"
          className="font-normal text-neutral-700"
          onClick={onContinue}
          disabled={isBusy}
          data-testid="retention-modal-continue-button"
        >
          {t('cancelSubscription.retention.continueButton')}
        </Button>
        <Button
          color="primary"
          size="lg"
          className="min-w-[210px] rounded-lg"
          onClick={onKeep}
          disabled={isBusy}
          data-testid="retention-modal-keep-button"
        >
          {t('cancelSubscription.retention.keepButton')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

/** One row of the left column: an icon, a label, and a value or a next-step hint. */
export function RetentionRow(props: {
  icon: typeof UsersIcon;
  label: string;
  value?: ReactNode;
  index: number;
  testId: string;
}) {
  const { icon: Icon, label, value, index, testId } = props;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 ${
        index % 2 === 0 ? 'bg-base-200' : 'bg-base-100'
      }`}
      data-testid={testId}
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon aria-hidden="true" className="h-4 w-4 text-primary" />
      </span>
      <span className="flex-1 text-base text-neutral-700">{label}</span>
      {value}
    </div>
  );
}
